// skipcq: JS-W1028, JS-0128
import React from 'react';
import {
  Box,
  CircularProgress,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { isSafeHttpUrlForLink } from '../../domain/eventDefinitionValidation';
import { DETAIL_MUTED, PRIMARY_DETAIL_TEXT } from '../../utils/theme';

/** Fixed header artboard: 400×450 CSS px; scales down on narrow containers. */
const headerImageFrameSx = {
  display: 'block',
  width: 400,
  maxWidth: '100%',
  height: 450,
  objectFit: 'cover',
  mx: 'auto',
};

export function EventDetailHero({ imageUrl, eventTitle }) {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        overflow: 'hidden',
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={eventTitle || 'Event'}
          referrerPolicy="no-referrer"
          sx={headerImageFrameSx}
        />
      ) : (
        <Box
          sx={{
            ...headerImageFrameSx,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography color="text.secondary">No header image</Typography>
        </Box>
      )}
    </Box>
  );
}

export function EventDetailLoadingState() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress />
    </Box>
  );
}

export function EventDetailErrorState({ message }) {
  return (
    <Typography
      role="alert"
      aria-live="polite"
      color="error"
      align="center"
      sx={{ py: 4 }}
    >
      {message}
    </Typography>
  );
}

export function EventDetailInfoCard({ viewModel }) {
  const titleSx = { fontWeight: 700, color: PRIMARY_DETAIL_TEXT, mb: 0.5 };
  const lineSx = { color: DETAIL_MUTED, mb: 0.5 };
  const ticketHref =
    viewModel.linkTrimmed && isSafeHttpUrlForLink(viewModel.linkTrimmed)
      ? viewModel.linkTrimmed
      : '';

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: 'text.primary',
        p: { xs: 2, sm: 3 },
        textAlign: 'center',
      }}
    >
      <Typography sx={titleSx} variant="h6" component="h1">
        {viewModel.hostDisplay || '—'}
      </Typography>
      <Typography sx={lineSx} variant="body1">
        {viewModel.eventName || 'Untitled'}
      </Typography>
      <Typography sx={lineSx} variant="body1">
        {viewModel.dateLine || '—'}
      </Typography>
      {viewModel.timeLine ? (
        <Typography sx={lineSx} variant="body1">
          {viewModel.timeLine}
        </Typography>
      ) : null}
      {viewModel.venueDisplay ? (
        <Typography sx={lineSx} variant="body1">
          @ {viewModel.venueDisplay}
        </Typography>
      ) : null}
      {viewModel.cityState ? (
        <Typography sx={{ color: DETAIL_MUTED, mb: 1 }} variant="body1">
          {viewModel.cityState}
        </Typography>
      ) : null}
      {ticketHref ? (
        <Link
          href={ticketHref}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontWeight: 700,
            color: PRIMARY_DETAIL_TEXT,
            textDecoration: 'underline',
            display: 'inline-block',
            mb: 2,
          }}
        >
          {viewModel.linkLabel}
        </Link>
      ) : (
        <Typography sx={{ color: DETAIL_MUTED, mb: 2 }} variant="body2">
          No ticket link yet
        </Typography>
      )}
      {viewModel.description ? (
        <Typography
          sx={{
            color: DETAIL_MUTED,
            textAlign: 'center',
            whiteSpace: 'pre-wrap',
          }}
          variant="body2"
        >
          {viewModel.description}
        </Typography>
      ) : null}
    </Paper>
  );
}
